import { GeoPoint, IGeoPoint, Media } from '@/classes'
import { SUBSCRIPTION_TYPES, SubscriptionStores } from '@/types/subscriptions'
import { INotification, Notification } from './Notification'
import { PRONOUNS } from '@/static/pronouns'
import { THEMES, THEMES_DARK } from '@/static/themes'
import { distanceGet } from '@/utils/native/useGeolocation'
import { IUserProfile, UserGenders } from './UserProfile'
import dayjs from 'dayjs'
import { SOCIALS } from '@/static/socials'

export const UserFlags = [
  'lesbian', 'bi', 'gay', 'trans', 'nonbinary', 'lgbtqia', 'ace', 'polyamory',  'intersex',

   'agender', 'aro', 'bear','bigender', 'demiboy', 'demigirl', 'demiromantic', 'demisexual', 'genderfluid', 'genderqueer', 'omni', 'pangender', 'pansexual',  'pride'
] as const

export const UserJourney = [
  'is-registered', 'is-onboarded', 'is-onboarded-2',

  'test-subscription-a', 'test-subscription-b',
  'test-discount-percentage-a', 'test-discount-percentage-b',
  
  'is-nps', 'is-orga-rewards',

  'info-calendar', 'info-explore', 'info-attend', 'info-calendar-button', 'info-share-place', 'info-vibe', 'info-profile', 'info-long-distance', 'info-create-post', 'info-community-filters','info-create-post', 'info-community-filters', 'info-friend', 'info-event-group', 'info-post-swipe', 'info-event-swipe', 'info-follow', 'info-intentions', 'info-onboarding', 'info-cheers', 'info-spicy', 'info-spicy-1', 'info-spicy-2', 'info-nav-community', 'info-nav-feed', 'info-event-pro', 'info-place-follow',

  'is-notifications',

  'free-boost-start',
  'free-boost',
  'free-boost-used',

  'post-created',
  'follow-3',

  'request-message',

  'crushes-disabled'
] as const

export const UserSpiceLevels = [ 'sweet', 'mild', 'hot' ] as const
export const UserTypes = [ 'member', 'pro', 'bot' ] as const
export const UserRolesOrga = ['normal', 'pro'] as const
export const UserRoles = [ 'guest', 'member', 'editor', 'admin', 'blocked' ] as const

export const UserLongDistanceTypes = ['decline', 'accept', 'active'] as const

export const UserIntentions = [ 'friends', 'dating', 'fling' ] as const
export const UserMBTI = {
  intj: {
    image: 'Classical+Building',
    type: 'a'
  },
  intp: {
    image: 'Test+Tube',
    type: 'a'
  },
  entj: {
    image: 'Military+Medal',
    type: 'a'
  },
  entp: {
    image: 'Microphone',
    type: 'a'
  },
  infj: {
    image: 'Balance+Scale',
    type: 'd'
  },
  infp: {
    image: 'Cherry+Blossom',
    type: 'd'
  },
  enfj: {
    image: 'Crossed+Swords',
    type: 'd'
  },
  enfp: {
    image: 'Red+Heart',
    type: 'd'
  },
  istj: {
    image: 'Light+Bulb',
    type: 's'
  },
  isfj: {
    image: 'Shield',
    type: 's'
  },
  estj: {
    image: 'Necktie',
    type: 's'
  },
  esfj: {
    image: 'Writing+Hand',
    type: 's'
  },
  istp: {
    image: 'Musical+Note',
    type: 'e'
  },
  isfp: {
    image: 'Paintbrush',
    type: 'e'
  },
  estp: {
    image: 'High+Voltage',
    type: 'e'
  },
  esfp: {
    image: 'Party+Popper',
    type: 'e'
  }
} as const

export type UserDevice = {
  id: string
  name: string
  isActive: boolean
  fcm: string
  refreshedAt: Date
  createdAt: Date
}

export type UserEntitlement = {
  _id: string
  level: string
  type: keyof typeof SUBSCRIPTION_TYPES
  store: typeof SubscriptionStores[number]
  manageUrl: string
}

export interface UserFriend {
  _id: string
  createdAt: Date
}

export type UserLanguage = {
  id: string
  level: number
}

export const MutableChannels = [ 'conversation', 'post', 'mail', 'push' ] as const

export type UserMuteChannel = {
  _id: string
  value?: string
  type: typeof MutableChannels[number]
  expiresAt: Date | null
}

export type UserCard = {
  id: string
  brand: string
  bin: string
  billing: {
    firstName: string
    lastName: string
    address: string
    city: string
    postalCode: string
    country: string
  }
}
export interface IUser {
  _id: string
  email?: string

  role: typeof UserRoles[number]
  roleOrga: typeof UserRolesOrga[number]
  type: typeof UserTypes[number]
  name: string
  lastname?: string
  username?: string
  dateOfBirth?: Date
  isSpicy: boolean
  
  geolocation: IGeoPoint
  geolocationPublic: IGeoPoint
  city: string

  language: string
  languages: UserLanguage[]

  isLangTranslate: boolean
  langTarget: string
  langExcluded: string[]

  lang: string

  links: {
    type: keyof typeof SOCIALS
    value: string
  }[]
  
  phone?: string

  image?: Media
  theme: keyof typeof THEMES
    
  attendedCount: number

  groups: string[]
  journey: typeof UserJourney[number][]

  // PROFILE

  mainFlag: (typeof UserFlags[number]) | ''
  pronouns: typeof PRONOUNS[number][]
  mbti: keyof typeof UserMBTI | ''

  profile: string
  ['@profile']?: IUserProfile

  organizations?: string[]
  backstage: string

  // SOCIAL

  friends: UserFriend[]
  requests: number
  cheers: number
  boosts: number
  messagePresets: string[]

  // SUBS

  entitlements: UserEntitlement[]

  discord?: {
    id?: string
    username?: string
  }

  // ORGANIZER PROFILE

  organizedCount: number

  genders: (typeof UserGenders[number])[]

  // FILTERS
  filters: {
    genders: (typeof UserGenders[number])[]
    isGendersStrict: boolean

    age: { min: number, max: number }
    isAgesStrict: boolean
    
    distance: number
    isDistancePostsStrict: boolean
    isDistanceStrict: boolean
  }

  // NOTIFICATIONS

  notifications: INotification[]
  mutedChannels: UserMuteChannel[]
  devices: UserDevice[]

  // OTHER

  isAmbassador: boolean
  blocked: string[]
  hiddenGroups: string[]
  completeExpiresAt?: Date | null
  offerExpiresAt?: Date | null
  squareCards?: UserCard[]
  stories: {
    _id: string
    createdAt: Date
  }[]

  boostExpiresAt?: Date | null

  // VIRTUALS
  isMessageable: boolean
  isIncognito: boolean
  isAds: boolean
  isAdsSponsored: boolean
  isInsider: boolean
  isContributor: boolean
  isSiloed?: boolean
  
  isUnavailable?: boolean
  
  longDistanceStatus: typeof UserLongDistanceTypes[number]
  experienceLevel?: 0 | 1 | 2 | 3 | 4 | 5 | 6

  // 0: 0-15 minutes
  // 1: 15-30 minutes
  // 2: 30-60 minutes
  // 3: 1-5 hours
  // 4: 5-10 hours
  // 5: 10-20 hours
  // 6: 20+ hours

  isGuest: boolean
  isMember: boolean
  isEditor: boolean
  isAdmin: boolean
  isOnboarded: boolean

  isSubscriber: boolean
  isReadMarks: boolean

  age: number
  attendanceGrade: 'perfect' | 'good' | 'mid' | 'bad' | 'new'
  friendsCount: number
  lastLogged: number
  hasMet?: boolean
  followStatus?: 'following' | 'follower' | 'mutual' | 'none'

  // Following: I follow them
  // Follower: They follow me

  intentions: {
    mine: (typeof UserIntentions[number])[]
    common: (typeof UserIntentions[number])[]
  }

  translations: {
    [key: string]: string
  }

  // DETAILS
  
  subscribedAt?: Date
  subscriberDiscountGrantedAt?: Date
  activeAt?: Date
  createdAt?: Date | null
  onboardedAt?: Date
  registeredAt?: Date | null
  emailVerifiedAt?: Date | null
  updatedAt?: Date
  lastVisitAt?: Date | null
}

export interface User extends IUser {}
export class User implements IUser {
  notifications: Notification[]
  geolocation: GeoPoint
  geolocationPublic: GeoPoint
  coordinatesPublic: [ number, number ] | null
  coordinates: [ number, number ] | null

  constructor(props: IUser) {
    
    Object.assign(this, {
      ...props,
      notifications: props.notifications ? props.notifications.map(n => new Notification(n)) : [],
      image: props.image ? new Media(props.image) : null,
            
      geolocation: new GeoPoint(props.geolocation),
      coordinates: new GeoPoint(props.geolocation).coordinates,
      geolocationPublic: new GeoPoint(props.geolocationPublic),
      coordinatesPublic: new GeoPoint(props.geolocationPublic).coordinates,
      boostExpiresAt: props.boostExpiresAt ? new Date(props.boostExpiresAt) : null,
      completeExpiresAt: props.completeExpiresAt ? new Date(props.completeExpiresAt) : null,
      createdAt: props.createdAt ? new Date(props.createdAt) : null,
    })
  }

  public get isRegistered () {
    return (this.journey ?? []).includes('is-registered')
  }

  public get isOnboarded2 () {
    return (this.journey ?? []).includes('is-onboarded-2')
  }

  public get hasStory () {
    return false
  }

  public get isClub () {
    return this.isSubscriber
  }
  
  public distance (coordinates?: [number, number] | null, options?: { isNumber?: boolean, isRaw?: boolean }) {
    const distanceKM = distanceGet(coordinates ?? [0, 0], this.coordinatesPublic ?? [0, 0])

    if (options?.isRaw) return distanceKM

    return `${Math.max(5, Math.ceil(distanceKM / 10) * 10)}km`
  }

  public isFriend (userId: string) {
    if (!this.friends) return false

    return this.friends.find(f => f._id === userId) ? true : false
  }

  public isMuted (channelId: string, type: typeof MutableChannels[number] = 'conversation') {
    if (!this.mutedChannels) return false

    return this.mutedChannels.find(channel => (channel._id === channelId || channel.value === channelId) && channel.type === type && (!channel.expiresAt || new Date() < new Date(channel.expiresAt) ))
  }

  public getWeeklyBestie (weeklyId?: string) {
    if (!weeklyId) return null
    
    return (this['@profile']?.weeklyBestie ?? []).find(w => w.id === weeklyId) ?? null
  }

  public isWeeklyBestieCompleted (weeklyId?: string) {
    if (!weeklyId) return true

    const wb = this.getWeeklyBestie(weeklyId)
    return wb && wb.answers.filter(a => a !== -1).length > 0
  }

  public getCompatibility (user: User | null, weeklyId?: string) {
    const mine = (this['@profile']?.weeklyBestie ?? []).find(w => w.id === weeklyId)
    const theirs = user && (user['@profile']?.weeklyBestie ?? []).find(w => w.id === weeklyId)
    
    if (mine && theirs) {
      const common = mine.answers.filter((a, i) => theirs.answers[i] === a)
      return Math.round(common.length / mine.answers.length * 100)
    }

    return null
  }

  public get isSolidarity () {
    const limit = dayjs().subtract(7, 'days')

    return this.subscriberDiscountGrantedAt && limit.isBefore(this.subscriberDiscountGrantedAt)
  }

  public translated (user: User | null, excludedIds?: string[]) {
    const isTranslationsEnabled = user?.isLangTranslate && !(user?.langExcluded ?? []).includes(this.lang ?? '') && !(excludedIds ?? []).includes(this._id)

    return {
      bio: (isTranslationsEnabled ? this.translations?.bio : undefined) ?? this['@profile']?.bio ?? ''
    }
  }
}
